import classNames from 'classnames'
import * as React from 'react'

import {makeEditable} from '@/common/utils'
import {Grid} from '@/components/Grid'

import {ComparisonLogo, isWitik} from '../helpers'

const ComparisonTable = ({_editable, solutionA, solutionB, story}) => {
  if (
    solutionA &&
    solutionB &&
    story.comparison_database &&
    Array.isArray(story.comparison_database.categories) &&
    Array.isArray(story.comparison_database.solutions)
  ) {
    const first = story.comparison_database.solutions.find(({name}) => name === solutionA)
    const second = story.comparison_database.solutions.find(({name}) => name === solutionB)

    if (first && second) {
      const solutions = [first, second]
      const categories = story.comparison_database.categories

      const columnsWidth = 'w-[90px] md:w-[150px] lg:w-[330px] flex-none text-center'

      return (
        <section {...makeEditable(_editable)} className='py-16 md:py-24'>
          <Grid>
            <div className='col-span-full'>
              <div className='flex justify-end'>
                {solutions.map(({_uid, name}, index) => {
                  const witik = isWitik(name)

                  return (
                    <div
                      key={_uid}
                      className={classNames(
                        columnsWidth,
                        'body-l-bold relative flex items-center justify-center border border-b-0 border-violet-300 px-1 py-4 text-violet-600 lg:py-6',
                        {
                          'bg-violet-600 !pt-0': witik,
                          'rounded-tl-3xl border-r-0': index === 0 && !witik,
                          'rounded-tr-3xl': index > 0 && !witik,
                        }
                      )}
                    >
                      {witik && (
                        <div className='absolute bottom-full left-0 h-4 w-full rounded-t-3xl bg-violet-600 lg:h-6' />
                      )}
                      {witik ? (
                        <ComparisonLogo />
                      ) : (
                        <div className='overflow-hidden overflow-ellipsis'>{name}</div>
                      )}
                    </div>
                  )
                })}
              </div>
              <div className='divide-y divide-white rounded-tl-3xl rounded-bl-3xl border border-violet-300 text-purple-900'>
                {categories.map((category, index, {length}) => {
                  const features = category.features || []

                  return (
                    <details
                      key={category._uid}
                      open={index === 0}
                      className='group divide-y divide-violet-300'
                    >
                      <summary
                        className={classNames(
                          'body-l-bold flex items-center gap-4 bg-violet-300 p-4 hover:cursor-pointer group-open:bg-violet-200 lg:p-8',
                          {
                            'rounded-tl-3xl': index === 0,
                            'rounded-bl-3xl group-open:rounded-bl-none': index === length - 1,
                          }
                        )}
                      >
                        <ChevronIcon />
                        {category.name}
                      </summary>

                      {features.map((categoryFeature) => {
                        const tooltipId = `${categoryFeature._uid}-tooltip`
                        return (
                          <div
                            key={categoryFeature._uid}
                            className='flex divide-x divide-violet-300'
                          >
                            <div className='body-m-bold flex flex-1 items-center justify-between px-2 py-4 md:justify-start lg:px-8 lg:py-6'>
                              <div className='hyphens-auto'>{categoryFeature.name}</div>
                              <div className='relative'>
                                <button
                                  aria-labelledby={tooltipId}
                                  className='-translate-y-[2px] py-2 pl-2'
                                >
                                  <InfoIcon />
                                </button>
                                <div
                                  role='tooltip'
                                  id={tooltipId}
                                  className='body-xs-regular pointer-events-none absolute bottom-full -left-10 mb-2 hidden w-screen max-w-[230px] rounded-3xl border border-white bg-violet-200 p-6 text-purple-900 md:max-w-xs lg:left-1/2 lg:-translate-x-1/2'
                                >
                                  {categoryFeature.description}
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    viewBox='0 0 7 6'
                                    className='absolute top-full left-12 h-3 w-[14px] lg:left-1/2 lg:-translate-x-1/2'
                                  >
                                    <path
                                      className='fill-violet-200'
                                      d='M0 0 l7 0 l-3.5 6 l-3.5 -6'
                                    />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            {solutions.map((solution) => {
                              const feature = solution.features.find(
                                ({featureId}) => featureId === categoryFeature.id
                              )
                              const {status, description} = feature || {}
                              const witik = isWitik(solution.name)

                              return (
                                <div
                                  key={solution._uid}
                                  className={classNames(
                                    columnsWidth,
                                    'flex flex-col justify-center gap-2 break-words py-4 px-1 text-center lg:p-8',
                                    {'bg-violet-200': witik}
                                  )}
                                >
                                  {!!status ? (
                                    <span className='block'>
                                      {status === 'true' ? <CheckIcon /> : <CloseIcon />}
                                    </span>
                                  ) : null}
                                  {!!description && (
                                    <span className='body-m-medium block'>{description}</span>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )
                      })}
                    </details>
                  )
                })}
              </div>
            </div>
          </Grid>
        </section>
      )
    }

    if (!first) return <Error _editable={_editable}>{solutionA} not found in the database</Error>
    if (!second) return <Error>{solutionB} not found in the database</Error>
  }

  if (!solutionA) return <Error>Solution A is missing</Error>
  if (!solutionB) return <Error _editable={_editable}>Solution B is missing</Error>

  if (!story.comparison_database) return <Error _editable={_editable}>Database not found</Error>

  return <Error _editable={_editable}>Internal server error</Error>
}

export const Error = ({_editable, children}) => (
  <p {...makeEditable(_editable)} className='body-m-bold py-10 text-center text-red-800'>
    Error: {children}
  </p>
)

const ChevronIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    fill='none'
    viewBox='0 0 16 16'
    className='h-4 w-4 -rotate-90 transition group-open:rotate-0'
  >
    <path
      className='stroke-purple-900'
      strokeLinecap='round'
      strokeWidth={2}
      d='M14.67 4.67 8 11.33 1.33 4.67'
    />
  </svg>
)

const InfoIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    fill='none'
    viewBox='0 0 16 16'
    className='h-4 w-4 translate-y-[2px]'
  >
    <path
      className='fill-violet-600'
      d='M8 14.5a5.96 5.96 0 0 1-5.2-3 6.01 6.01 0 0 1 10.38-6 5.97 5.97 0 0 1 0 6 5.96 5.96 0 0 1-5.18 3Zm-.94-4.13a.56.56 0 0 0-.56.57c0 .33.23.56.56.56h1.88c.3 0 .56-.23.56-.56 0-.3-.26-.56-.56-.56h-.19V8.3c0-.3-.26-.56-.56-.56H7.06a.56.56 0 0 0-.56.56c0 .33.23.56.56.56h.57v1.5h-.57ZM8 5.5a.76.76 0 0 0-.75.75c0 .42.33.75.75.75.4 0 .75-.33.75-.75 0-.4-.35-.75-.75-.75Z'
    />
  </svg>
)

const CheckIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    fill='none'
    viewBox='0 0 24 24'
    className='mx-auto'
  >
    <path
      className='stroke-purple-900'
      strokeLinecap='round'
      strokeWidth='1.5'
      d='M2 12.88 8.4 19 23 5'
    />
    <path
      className='stroke-purple-900'
      strokeLinecap='round'
      strokeOpacity='.2'
      strokeWidth='1.5'
      d='M2 12.88 8.4 19 23 5'
    />
  </svg>
)

const CloseIcon = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className='mx-auto'
  >
    <path
      d='M5 5L19.1421 19.1421'
      className='stroke-red-800'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
    <path
      d='M5 19L19.1421 4.85786'
      className='stroke-red-800'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
  </svg>
)

export default ComparisonTable
